// src/services/fishIQClient.ts
import { onAuthStateChanged } from 'firebase/auth';
import { httpsCallable, type HttpsCallableResult } from 'firebase/functions';
import { auth, functions } from '../lib/firebase';
import { FEATURES } from '../constants/features';
import { isProbablyNetworkError } from './netSafe';

function fakeResult<T>(data: T): HttpsCallableResult<T> {
  return { data } as any;
}

async function waitForJsAuth(timeoutMs = 6000): Promise<string | null> {
  if (auth.currentUser?.uid) return auth.currentUser.uid;

  return new Promise((resolve) => {
    let done = false;

    const finish = (uid: string | null) => {
      if (done) return;
      done = true;
      try {
        unsub();
      } catch {}
      resolve(uid);
    };

    const t = setTimeout(() => finish(null), timeoutMs);

    const unsub = onAuthStateChanged(auth, (u) => {
      if (u?.uid) {
        clearTimeout(t);
        finish(u.uid);
      }
    });
  });
}

function authedCallable<TReq, TRes extends { ok?: boolean; error?: string }>(
  name: string,
  opts?: { authTimeoutMs?: number; softFailOk?: boolean }
) {
  const fn = httpsCallable<TReq, TRes>(functions, name);

  return async (payload: TReq): Promise<HttpsCallableResult<TRes>> => {
    try {
      const uid = await waitForJsAuth(opts?.authTimeoutMs ?? 6000);

      if (!uid) {
        return fakeResult({
          ok: false,
          error: 'no_auth',
        } as TRes);
      }

      const res = await fn(payload);
      return res;
    } catch (e: any) {
      if (isProbablyNetworkError(e)) {
        console.warn(`[fishIQ] ${name} network blocked; soft-failing`, e);
        return fakeResult({
          ok: false,
          error: 'network_blocked',
        } as TRes);
      }

      console.warn(`[fishIQ] ${name} failed; soft-failing`, e);
      return fakeResult({
        ok: false,
        error: e?.message ?? String(e),
      } as TRes);
    }
  };
}

export const fishIQBootstrapUser = authedCallable<
  { uid?: string },
  {
    ok: boolean;
    alreadyBootstrapped?: boolean;
    memberNumber?: number;
    earlyPro?: boolean;
    isPro?: boolean;
    error?: string;
  }
>('fishIQBootstrapUser');

export const fishIQIngestUrl = authedCallable<any, any>('fishIQIngestUrl');

type FishIQSignal = {
  signalId?: string;
  id?: string;
  geo5: string;
  timeBucket: string;
  speciesKey: string;
  waterKey: string;
  waterTypeKey?: string;
  aquaticKind?: string;
  count24h: number;
  count7d?: number;
  count30d?: number;
  uniqueUsers24h: number;
  uniqueUsers7d?: number;
  lastSeenAtMs?: number | null;
  lastSeenAt?: number | null;
  topTechniques: string[];
  topLures: string[];
  bestTimeBuckets?: string[];
  topObservationTypes?: string[];
  topActivityClues?: string[];
  topBiteOutcomes?: string[];
  topWaterClarities?: string[];
  topWaterLevels?: string[];
  topPressureTrends?: string[];
  topMoonPhases?: string[];
  evidenceQualityBand?: 'high' | 'good' | 'usable' | 'limited' | string;
  averageObservationQuality?: number;
  activeDays?: number;
  activeMonths?: number;
  recencyBucket?: 'recent' | 'today' | 'older' | string;
  activityScore?: number;
  activityRank?: number;
  activityBand?: 'most_active' | 'active' | 'possible' | 'low_signal' | string;
  catchabilityScore?: number | null;
  catchabilityBand?: 'hot' | 'active' | 'mixed' | 'quiet' | 'unknown' | string;
  positiveActivityCount?: number;
  noCatchCount?: number;
  catchabilitySampleCount?: number;
  regulatoryFlags?: string[];
};

type FishIQSignalsReq = {
  lat: number;
  lon: number;
  waterType?: string | null;
  waterKind?: string | null;
  waterName?: string | null;
  regionHint?: string | null;
  speciesName?: string | null;
  forceRefresh?: boolean;
};

type FishIQSignalsRes = {
  ok: boolean;
  geo5?: string;
  gated?: boolean;
  error?: string;
  cached?: boolean;
  signals?: FishIQSignal[];
};

const fishIQGetSignalsNearMeCallable = authedCallable<FishIQSignalsReq, FishIQSignalsRes>('fishIQGetSignalsNearMe');

const SIGNALS_CLIENT_CACHE_KEY = 'roddo.fishiq.signalsCallableCache.v1';
const SIGNALS_CLIENT_TTL_MS = 30 * 60 * 1000;
const SIGNALS_CLIENT_STALE_MS = 24 * 60 * 60 * 1000;

const SALT_WATER_HINTS = ['ocean', 'sea', 'gulf', 'bay', 'inshore', 'estuary', 'harbor', 'harbour', 'marina', 'coast', 'surf', 'salt'];
const FRESH_WATER_HINTS = ['lake', 'pond', 'reservoir', 'river', 'creek', 'stream', 'canal', 'freshwater'];

function inferWaterRealm(payload: FishIQSignalsReq): 'salt' | 'fresh' | 'unknown' {
  const hay = `${payload.waterType ?? ''} ${payload.waterKind ?? ''} ${payload.waterName ?? ''}`.toLowerCase();
  if (SALT_WATER_HINTS.some((hint) => hay.includes(hint))) return 'salt';
  if (FRESH_WATER_HINTS.some((hint) => hay.includes(hint))) return 'fresh';
  return 'unknown';
}

function roundedCoord(value: number) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0.000';
  return (Math.round(n * 10000) / 10000).toFixed(4);
}

function normalizedWaterIdentity(payload: FishIQSignalsReq) {
  return `${payload.waterName ?? ''}|${payload.waterType ?? ''}|${payload.waterKind ?? ''}`
    .toLowerCase()
    .replace(/[^a-z0-9|]+/g, '_')
    .slice(0, 160);
}

function signalsCacheKey(payload: FishIQSignalsReq) {
  // Do not include speciesName: the backend returns an area aggregate, not a per-search result.
  // Include the resolved water identity and a tighter local coordinate bucket so moving between
  // nearby but different waters cannot silently reuse a stale FishIQ signal set.
  return `${roundedCoord(payload.lat)},${roundedCoord(payload.lon)}:${inferWaterRealm(payload)}:${normalizedWaterIdentity(payload)}:${payload.regionHint ?? ''}`;
}

function readSignalsClientCache(payload: FishIQSignalsReq, maxAgeMs = SIGNALS_CLIENT_TTL_MS): FishIQSignalsRes | null {
  try {
    if (typeof window === 'undefined') return null;
    const raw = window.localStorage.getItem(SIGNALS_CLIENT_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const key = signalsCacheKey(payload);
    const entry = parsed?.[key];
    if (!entry || typeof entry !== 'object') return null;
    if (!Array.isArray(entry?.data?.signals)) return null;
    const age = Date.now() - Number(entry.ts || 0);
    if (age > maxAgeMs) return null;
    return { ...(entry.data as FishIQSignalsRes), cached: true };
  } catch {
    return null;
  }
}

function writeSignalsClientCache(payload: FishIQSignalsReq, data: FishIQSignalsRes) {
  try {
    if (typeof window === 'undefined' || !data?.ok) return;
    const raw = window.localStorage.getItem(SIGNALS_CLIENT_CACHE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    const key = signalsCacheKey(payload);
    parsed[key] = { ts: Date.now(), data };

    // Keep the cache tiny. It is only a tap/read shield, not a database.
    const entries = Object.entries(parsed as Record<string, any>)
      .sort((a, b) => Number(b[1]?.ts || 0) - Number(a[1]?.ts || 0))
      .slice(0, 12);

    window.localStorage.setItem(SIGNALS_CLIENT_CACHE_KEY, JSON.stringify(Object.fromEntries(entries)));
  } catch {}
}

export async function fishIQGetSignalsNearMe(payload: FishIQSignalsReq): Promise<HttpsCallableResult<FishIQSignalsRes>> {
  if (!FEATURES.FISHIQ_LIVE_SIGNALS) {
    return fakeResult({ ok: true, signals: [], gated: false, cached: true });
  }

  const cached = !payload.forceRefresh ? readSignalsClientCache(payload, SIGNALS_CLIENT_TTL_MS) : null;
  if (cached) return fakeResult(cached);

  const res = await fishIQGetSignalsNearMeCallable(payload);
  const data = (res as any)?.data as FishIQSignalsRes;

  if (data?.ok) {
    writeSignalsClientCache(payload, data);
    return res;
  }

  // Last-resort stale fallback when callable/auth/network soft-fails.
  const stale = readSignalsClientCache(payload, SIGNALS_CLIENT_STALE_MS);
  if (stale) return fakeResult(stale);

  return res;
}

export const bootstrapMemberAndAccess = authedCallable<
  { inviteCode?: string },
  {
    ok: boolean;
    uid?: string;
    memberNumber?: number;
    accessStatus?: 'active' | 'waitlisted' | 'blocked';
    waitlistNumber?: number;
    error?: string;
  }
>('bootstrapMemberAndAccess');


export const submitFishIQObservation = authedCallable<
  {
    source?: string;
    sourceId?: string;
    speciesName: string;
    weight?: string | number | null;
    technique?: string;
    lureOrBait?: string;
    weather?: string;
    createdAtMs?: number;
    coords?: { lat: number; lon: number } | null;
    waterName?: string | null;
    timeBucket?: string;
    month?: string;
    shareToCommunityPool?: boolean;
    consentVersion?: string;
    privacyMode?: 'private_only' | 'generalized_aggregate' | string;
    regionKey?: string | null;
    pilotPartnerCode?: string | null;
    pilotCohortCode?: string | null;
    researchObservationVersion?: string | null;
    observationType?:
      | 'catch'
      | 'missed_bite'
      | 'follow'
      | 'surface_activity'
      | 'baitfish_seen'
      | 'birds_working'
      | 'schooling_seen'
      | 'no_catch_session'
      | string;
    activityClues?: string[];
    biteOutcome?: 'landed' | 'missed' | 'follow' | 'observed_activity' | 'no_catch' | 'unknown' | string;
    missedBiteCount?: number | null;
    followCount?: number | null;
    weatherSnapshot?: {
      conditionText?: string | null;
      temp?: number | null;
      tempUnit?: 'Celsius' | 'Fahrenheit' | string | null;
      windText?: string | null;
      windSpeed?: number | null;
      windDirection?: string | null;
      pressureTrend?: 'rising' | 'falling' | 'steady' | 'unknown' | string | null;
      cloudCover?: string | null;
      moonPhase?: string | null;
    } | null;
    waterSnapshot?: {
      waterCondition?: string | null;
      waterClarity?: 'clear' | 'stained' | 'muddy' | 'unknown' | string;
      waterLevel?: 'low' | 'normal' | 'high' | 'unknown' | string;
      tideStage?: string | null;
      currentFlow?: string | null;
      waterTemp?: number | null;
      waterTempUnit?: 'Celsius' | 'Fahrenheit' | string | null;
    } | null;
    waterClarity?: 'clear' | 'stained' | 'muddy' | 'unknown' | string;
    waterLevel?: 'low' | 'normal' | 'high' | 'unknown' | string;
    catchDisposition?: 'released' | 'harvested' | 'unknown' | string;
    effortMinutes?: number | null;
  },
  {
    ok: boolean;
    eventId?: string;
    sharedEventId?: string | null;
    error?: string;
  }
>('fishIQSubmitObservation');
